import {
    Alert,
    Backdrop,
    Box,
    Button,
    Chip,
    CircularProgress,
    CssBaseline,
    Grid,
    Paper,
    Snackbar,
    Stack,
    Toolbar,
    Typography,
    styled,
} from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import { axiosClient } from "../AxiosClient";
import { formatCurrency, formatDateTime } from "../contabilidadSiigo/siigoHelpers";

const Item = styled(Paper)(({ theme }) => ({
    backgroundColor: theme.palette.mode === "dark" ? "#1A2027" : "#fff",
    ...theme.typography.body2,
    padding: theme.spacing(1),
    textAlign: "center",
    color: theme.palette.text.secondary,
}));

const ContentItem = styled(Paper)(({ theme }) => ({
    backgroundColor: theme.palette.mode === "dark" ? "#1A2027" : "#fff",
    ...theme.typography.body2,
    padding: theme.spacing(2),
    color: theme.palette.text.primary,
}));

const InfoCard = styled(Paper)(({ theme }) => ({
    backgroundColor: theme.palette.mode === "dark" ? "#1A2027" : "#fff",
    padding: theme.spacing(2),
    color: theme.palette.text.primary,
    height: "100%",
    width: "100%",
    borderRadius: theme.spacing(1.5),
    border: "1px solid rgba(22, 30, 85, 0.1)",
    boxShadow: "0 2px 8px rgba(22, 30, 85, 0.12)",
}));

const ButtonPrincipal = styled(Button)({
    flexGrow: 0,
    borderRadius: "20px",
    gap: "10px",
    backgroundColor: "#161e55",
    color: "#ffff",
    "&:hover": {
        backgroundColor: "#A8C8AF",
    },
});

const ButtonReject = styled(Button)({
    flexGrow: 0,
    borderRadius: "20px",
    gap: "10px",
    backgroundColor: "#d32f2f",
    color: "#ffff",
    "&:hover": {
        backgroundColor: "#b71c1c",
    },
});

const ButtonSecondary = styled(Button)({
    flexGrow: 0,
    borderRadius: "20px",
    gap: "10px",
    color: "#161e55",
    borderColor: "#161e55",
});

const INVOICE_STATUS_LABELS = {
    validacion: "Validación",
};

const getInvoiceStatusLabel = (status) =>
    INVOICE_STATUS_LABELS[status] || status || "—";

const getErrorMessage = (error, fallback) =>
    error?.response?.data?.message || error?.message || fallback;

const DetailField = ({ label, value }) => (
    <Box mb={2}>
        <Typography variant="caption" color="text.secondary" display="block">
            {label}
        </Typography>
        <Typography variant="body1">{value ?? "—"}</Typography>
    </Box>
);

export default function ValidacionFacturaDetalle() {
    const { invoiceUuid } = useParams();
    const navigate = useNavigate();
    const [invoice, setInvoice] = useState(null);
    const [loading, setLoading] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarSeverity, setSnackbarSeverity] = useState("info");

    const showSnackbar = (message, severity = "info") => {
        setSnackbarMessage(message);
        setSnackbarSeverity(severity);
        setSnackbarOpen(true);
    };

    const fetchInvoice = useCallback(async () => {
        setLoading(true);
        try {
            const response = await axiosClient.get(`/v1/commerce-invoices/${invoiceUuid}`);
            setInvoice(response?.data || null);
        } catch (error) {
            showSnackbar(
                getErrorMessage(error, "No fue posible cargar el detalle de la factura."),
                "error"
            );
            setInvoice(null);
        } finally {
            setLoading(false);
        }
    }, [invoiceUuid]);

    useEffect(() => {
        fetchInvoice();
    }, [fetchInvoice]);

    const patchInvoice = async (action) => {
        setActionLoading(true);
        try {
            await axiosClient.patch(`/v1/commerce-invoices/${invoiceUuid}`, { action });
            showSnackbar(
                action === "approve"
                    ? "Factura validada correctamente."
                    : "Factura rechazada correctamente.",
                "success"
            );
            navigate("/homeAdmin/comercios/facturas");
        } catch (error) {
            showSnackbar(
                getErrorMessage(error, "No fue posible actualizar la factura."),
                "error"
            );
        } finally {
            setActionLoading(false);
        }
    };

    const handleApprove = () => {
        Swal.fire({
            title: "¿Desea validar esta factura?",
            showCancelButton: true,
            confirmButtonColor: "#161e55",
            cancelButtonColor: "#387A9C",
            confirmButtonText: "Validar",
            cancelButtonText: "Cancelar",
        }).then((result) => {
            if (result.isConfirmed) {
                patchInvoice("approve");
            }
        });
    };

    const handleReject = () => {
        Swal.fire({
            title: "¿Desea rechazar esta factura?",
            showCancelButton: true,
            confirmButtonColor: "#161e55",
            cancelButtonColor: "#387A9C",
            confirmButtonText: "Rechazar",
            cancelButtonText: "Cancelar",
        }).then((result) => {
            if (result.isConfirmed) {
                patchInvoice("reject");
            }
        });
    };

    const handleOpenPdf = () => {
        if (invoice?.invoiceFileUrl) {
            window.open(invoice.invoiceFileUrl, "_blank", "noopener,noreferrer");
        }
    };

    const renderValidationReasons = (reasons) => {
        if (!reasons?.length) {
            return <Typography variant="body1">—</Typography>;
        }
        return (
            <Stack direction="row" flexWrap="wrap" gap={0.5} mt={0.5}>
                {reasons.map((reason) => (
                    <Chip key={reason} label={reason} size="small" color="warning" variant="outlined" />
                ))}
            </Stack>
        );
    };

    return (
        <>
            <CssBaseline />
            <Toolbar />
            <Box sx={{ width: "100%" }}>
                <Stack spacing={2}>
                    <Item>.</Item>
                    <Item />
                    <Grid container spacing={2}>
                        <Grid item>
                            <Box display="flex" alignItems="center" flexWrap="wrap" gap={1}>
                                <h1>Detalle de factura</h1>
                                <ButtonSecondary variant="outlined" onClick={() => navigate("/homeAdmin/comercios/facturas")}>
                                    Volver
                                </ButtonSecondary>
                            </Box>
                        </Grid>
                    </Grid>

                    {loading ? (
                        <ContentItem>
                            <Typography textAlign="center">Cargando contenido</Typography>
                        </ContentItem>
                    ) : invoice ? (
                        <>
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={6} sx={{ display: "flex" }}>
                                    <InfoCard>
                                        <Typography variant="h6" gutterBottom>
                                            Datos del crédito
                                        </Typography>
                                        <DetailField label="ID Crédito" value={invoice.creditId} />
                                        <DetailField label="Monto" value={formatCurrency(invoice.creditAmount)} />
                                        <DetailField label="Fecha origen" value={invoice.originDate} />
                                        <DetailField label="Fecha desembolso" value={invoice.disbursementDate} />
                                    </InfoCard>
                                </Grid>
                                <Grid item xs={12} md={6} sx={{ display: "flex" }}>
                                    <InfoCard>
                                        <Typography variant="h6" gutterBottom>
                                            Comercio y cliente
                                        </Typography>
                                        <DetailField label="Comercio" value={invoice.commerceName} />
                                        <DetailField label="Cliente" value={invoice.clientName} />
                                        <DetailField label="Documento" value={invoice.clientDocumentNumber} />
                                    </InfoCard>
                                </Grid>
                                <Grid item xs={12} md={6} sx={{ display: "flex" }}>
                                    <InfoCard>
                                        <Typography variant="h6" gutterBottom>
                                            Estado y validación
                                        </Typography>
                                        <Box mb={2}>
                                            <Typography variant="caption" color="text.secondary" display="block">
                                                Estado
                                            </Typography>
                                            <Chip
                                                label={getInvoiceStatusLabel(invoice.invoiceStatus)}
                                                size="small"
                                                color="info"
                                                sx={{ mt: 0.5 }}
                                            />
                                        </Box>
                                        <Box mb={2}>
                                            <Typography variant="caption" color="text.secondary" display="block">
                                                Motivos de validación
                                            </Typography>
                                            {renderValidationReasons(invoice.validationReasons)}
                                        </Box>
                                    </InfoCard>
                                </Grid>
                                <Grid item xs={12} md={6} sx={{ display: "flex" }}>
                                    <InfoCard>
                                        <Typography variant="h6" gutterBottom>
                                            Información de carga
                                        </Typography>
                                        <DetailField label="Fecha de carga" value={formatDateTime(invoice.uploadedAt)} />
                                        <DetailField label="Cargado por" value={invoice.uploadedByUsername} />
                                        <DetailField label="Archivo" value={invoice.invoiceFileName} />
                                    </InfoCard>
                                </Grid>
                                <Grid item xs={12}>
                                    <ContentItem>
                                        <Box
                                            display="flex"
                                            alignItems="center"
                                            justifyContent="space-between"
                                            flexWrap="wrap"
                                            gap={1}
                                            mb={2}
                                        >
                                            <Typography variant="h6">Archivo de factura</Typography>
                                            {invoice.invoiceFileUrl && (
                                                <ButtonSecondary
                                                    variant="outlined"
                                                    startIcon={<OpenInNewIcon />}
                                                    onClick={handleOpenPdf}
                                                >
                                                    Abrir en nueva pestaña
                                                </ButtonSecondary>
                                            )}
                                        </Box>
                                        {invoice.invoiceFileUrl ? (
                                            <iframe
                                                src={invoice.invoiceFileUrl}
                                                width="100%"
                                                height="600px"
                                                title="Visor de factura"
                                                loading="lazy"
                                            />
                                        ) : (
                                            <Typography color="text.secondary" textAlign="center" py={4}>
                                                Archivo no disponible
                                            </Typography>
                                        )}
                                    </ContentItem>
                                </Grid>
                            </Grid>

                            <ContentItem>
                                <Stack direction="row" spacing={2} flexWrap="wrap" justifyContent="center">
                                    <ButtonPrincipal
                                        variant="contained"
                                        disabled={actionLoading}
                                        onClick={handleApprove}
                                    >
                                        {actionLoading ? <CircularProgress size={20} color="inherit" /> : "Validar"}
                                    </ButtonPrincipal>
                                    <ButtonReject
                                        variant="contained"
                                        disabled={actionLoading}
                                        onClick={handleReject}
                                    >
                                        Rechazar
                                    </ButtonReject>
                                    <ButtonSecondary
                                        variant="outlined"
                                        disabled={actionLoading}
                                        onClick={() => navigate("/homeAdmin/comercios/facturas")}
                                    >
                                        Volver
                                    </ButtonSecondary>
                                </Stack>
                            </ContentItem>
                        </>
                    ) : (
                        <ContentItem>
                            <Typography textAlign="center">
                                No se encontró la factura solicitada.
                            </Typography>
                        </ContentItem>
                    )}
                </Stack>
            </Box>
            <Toolbar />
            <Backdrop
                open={loading || actionLoading}
                sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
            >
                <CircularProgress color="inherit" />
            </Backdrop>
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={5000}
                onClose={() => setSnackbarOpen(false)}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert
                    onClose={() => setSnackbarOpen(false)}
                    severity={snackbarSeverity}
                    sx={{ width: "100%" }}
                >
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </>
    );
}
